import React, { useEffect, useRef, useState } from 'react';
import { HP2 } from './assets';

const PILLARS = [
  { key: 'prepared-gloves', label: 'Prepared', start: 1 },
  { key: 'systematic-office', label: 'Systematic', start: 1 },
  { key: 'detail-driven', label: 'Detail-Driven', start: 30 },
  { key: 'verified', label: 'Verified', start: 3 },
];
const DEFINITIONS = [
  ['Prepared', 'Understand the space and the work ahead. Preparation sets the direction before the cleaning begins.'],
  ['Systematic', 'Give the work a clear order. A deliberate process keeps the focus on the whole space.'],
  ['Detail-Driven', 'Look beyond the obvious. The edges, corners, and finishing touches deserve attention, too.'],
  ['Verified', 'Take a final look at the result. Review the work against the scope, rather than relying on a first impression.'],
];
const LOOP_SECONDS = 8;
const AUTO_ADVANCE_MS = 5500;
const isDesktop = () => window.matchMedia('(min-width:851px)').matches;
const motionOff = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const GuruStandard = () => {
  const pillarsRef = useRef(null);
  const videoRefs = useRef([]);
  const state = useRef({ index: -1, timer: null, visible: false, touched: false });
  const [active, setActive] = useState(-1);
  const [playing, setPlaying] = useState(-1);
  const [failed, setFailed] = useState({});
  const [spring, setSpring] = useState(-1);

  const pauseAll = () => {
    clearTimeout(state.current.timer);
    videoRefs.current.forEach((v) => v && v.pause());
    setPlaying(-1);
  };

  const run = (i) => {
    pauseAll();
    state.current.index = i;
    setActive(i);
    if (!state.current.visible || motionOff()) return;
    const v = videoRefs.current[i];
    if (!v) return;
    v.muted = true;
    v.play().then(() => {
      if (state.current.index !== i || !state.current.visible) { v.pause(); return; }
      setPlaying(i);
      if (!state.current.touched) state.current.timer = setTimeout(() => run((i + 1) % PILLARS.length), AUTO_ADVANCE_MS);
    }).catch((err) => { if (err?.name !== 'AbortError') setFailed((f) => ({ ...f, [i]: true })); });
  };

  useEffect(() => {
    const s = state.current;
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      s.visible = e.isIntersecting;
      if (s.visible) {
        if (isDesktop() && !motionOff()) run(s.index < 0 ? 0 : s.index);
        else if (s.index < 0) { s.index = 0; setActive(0); }
      } else pauseAll();
    }), { threshold: 0.25 });
    if (pillarsRef.current) io.observe(pillarsRef.current);

    const onVisibility = () => {
      if (document.hidden) pauseAll();
      else if (s.visible && s.index >= 0 && !motionOff() && isDesktop()) run(s.index);
    };
    const desktopMq = window.matchMedia('(min-width:851px)');
    const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onDesktopChange = () => { pauseAll(); if (desktopMq.matches && s.visible && !motionMq.matches) run(s.index < 0 ? 0 : s.index); };
    const onMotionChange = () => { if (motionMq.matches) pauseAll(); };
    document.addEventListener('visibilitychange', onVisibility);
    desktopMq.addEventListener('change', onDesktopChange);
    motionMq.addEventListener('change', onMotionChange);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      desktopMq.removeEventListener('change', onDesktopChange);
      motionMq.removeEventListener('change', onMotionChange);
      pauseAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPillarClick = (i) => {
    state.current.touched = true;
    if (state.current.index === i && playing === i) pauseAll();
    else run(i);
    setSpring(i);
  };

  const onPointerEnter = (i, e) => {
    if (e.pointerType === 'mouse' && isDesktop()) { state.current.touched = true; run(i); }
  };

  const onFocus = (i) => { if (isDesktop()) { state.current.touched = true; run(i); } };

  const seekStart = (i) => { const v = videoRefs.current[i]; if (v) v.currentTime = PILLARS[i].start; };
  const onTimeUpdate = (i) => {
    const v = videoRefs.current[i];
    if (v && v.currentTime > PILLARS[i].start + LOOP_SECONDS) v.currentTime = PILLARS[i].start;
  };

  return (
    <section className="guru-standard" id="guru-standard" aria-label="The Guru Standard" data-testid="guru-standard-section">
      <picture className="standard-art">
        <img className="standard-wide" src={HP2.guruStandardWide} alt="The Guru Standard" />
        <img className="standard-stack" src={HP2.guruStandardStack} alt="The Guru Standard" />
      </picture>
      <div className="standard-line" />
      <div className="standard-pillars" ref={pillarsRef}>
        {PILLARS.map((p, i) => {
          const cls = ['standard-pillar', active === i && 'active', playing === i && 'playing', failed[i] && 'video-error', spring === i && 'selected'].filter(Boolean).join(' ');
          return (
            <button
              key={p.key}
              className={cls}
              aria-pressed={active === i ? 'true' : 'false'}
              type="button"
              onClick={() => onPillarClick(i)}
              onPointerEnter={(e) => onPointerEnter(i, e)}
              onFocus={() => onFocus(i)}
              onAnimationEnd={(e) => { if (e.animationName === 'pillarSpring') setSpring(-1); }}
              data-testid={`pillar-${p.key}`}
            >
              <video
                ref={(el) => { videoRefs.current[i] = el; }}
                className="pillar-film"
                src={HP2.media(`${p.key}.mp4`)}
                muted
                playsInline
                loop
                preload={isDesktop() ? 'auto' : 'metadata'}
                aria-hidden="true"
                onLoadedMetadata={() => seekStart(i)}
                onTimeUpdate={() => onTimeUpdate(i)}
                onError={() => setFailed((f) => ({ ...f, [i]: true }))}
              />
              <img className="pillar-poster" src={HP2.media(`${p.key}.jpg`)} alt="" aria-hidden="true" />
              <span className="pillar-play" aria-hidden="true">{playing === i ? 'Ⅱ' : '▶'}</span>
              <span className="pillar-number">{`0${i + 1}`}</span>
              <strong>{p.label}</strong>
            </button>
          );
        })}
      </div>
      <div className="standard-explanation">
        <div>
          <h2>What we mean by<br /><span>THE GURU STANDARD.</span></h2>
          <p>The Guru Standard brings four principles together: preparation, a clear process, attention to detail, and a final review. It gives purpose to the work, from getting ready to checking the result.</p>
          <p className="standard-belief">We stand by it because the care behind a clean matters just as much as the way it looks.</p>
        </div>
        <dl className="standard-definitions">
          {DEFINITIONS.map(([term, def]) => (
            <div key={term}><dt>{term}</dt><dd>{def}</dd></div>
          ))}
        </dl>
      </div>
    </section>
  );
};
